import React, { useRef, useEffect, useState } from "react";
import tw, { css, styled } from "twin.macro";
import { graphql, useStaticQuery } from "gatsby";
import useBasin from "../hooks/useBasin";
import AnimateHeight from "react-animate-height";

import Input from "./FormInput";
import Button from "./Button";
import FormDisclaimer from "./FormDisclaimer";

const FormComponent = styled.form(({ sidebar }) => [
  tw`relative grid items-center w-full grid-cols-1 gap-24`,
  sidebar && tw`gap-12`,
]);

const Grid = styled.div(({ cols, sidebar }) => [
  tw`gap-x-8 gap-y-20 grid w-full`,
  sidebar && tw`gap-y-0`,

  css`
    grid-template-columns: repeat(${cols}, 1fr);
  `,
]);

const Response = styled.div(({ error }) => [
  tw`pt-16 font-bold text-center`,
  error === true && tw`text-red-500`,
]);

const Form = ({ sidebar, fields, cols = 3, ...rest }) => {
  const { endpoint } = useStaticQuery(graphql`
    query {
      wp {
        esquireAcf {
          firmInformation {
            endpoint: basinFormEndpoint
          }
        }
      }
    }
  `).wp.esquireAcf.firmInformation;

  const [{ submitted, pending, error }, submit] = useBasin(endpoint);
  const formRef = useRef();

  // add reacty crap to the fields
  fields = fields.map(({ ...rest }, i) => ({
    ...rest,
    ref: useRef(),
    valid: null,
    errorMessage: "",
  }));

  const [statefullFields, setStatefullFields] = useState(fields);

  /**
   * Checks an input element for correctness
   * and also returns its validation message.
   *
   * @param input   Input element to validate
   * @param strict  Flag to enable strict mode, where empty required inputs will error.
   * @returns       An array with two elements, the validity and the error message.
   */
  function validate(input, strict = false) {
    // If not in strict mode, return null instead of false,
    // because the input shouldn't error if it is empty while editing.
    let valid =
      strict === false && input.value === "" ? null : input.checkValidity();
    let errorMessage = input.validationMessage;

    if (
      valid &&
      strict &&
      input.tagName === "TEXTAREA" &&
      !(input.value.length > 30)
    ) {
      errorMessage = "Please tell us a little more.";
      valid = false;
    }

    return [valid, errorMessage];
  }

  /**
   * Takes the index of an input, validates it, and
   * updates its state if needed.
   *
   * @param i The index of the field.
   */

  const onChange = (i) => {
    const state = [...statefullFields];
    ``;
    const field = state[i];
    const input = field.ref.current;

    const [valid, errorMessage] = validate(input);

    // If the state is new, update it.
    if (errorMessage !== field.errorMessage || valid !== field.valid) {
      state[i].errorMessage = errorMessage;
      state[i].valid = valid;

      setStatefullFields(state);
    }
  };

  /**
   * Validates all inputs of a form and submits it to FormCarry.
   * If not valid, will update the form's state to reflect the errors.
   *
   * @param e The FormEvent
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const state = [...statefullFields];
    // Validate all elements

    if (
      !submitted &&
      state.every(({ ref }, i) => {
        if (ref.current) {
          const [valid, errorMessage] = validate(ref.current, true);
          statefullFields[i].valid = valid;
          statefullFields[i].errorMessage = errorMessage;
          return valid;
        } else return false;
      })
    )
      // Submit if all inputs are valid
      submit(e);
    // Otherwise, set the state to error incorrect fields.
    else setStatefullFields(state);
  };

  // Resets the form whenever it is succesfully submitted.
  useEffect(() => {
    (submitted || error) && formRef.current?.reset();
  }, [submitted, error]);

  return (
    <>
      <FormComponent
        ref={formRef}
        onSubmit={handleSubmit}
        sidebar={sidebar}
        {...rest}
      >
        <Grid cols={cols} sidebar={sidebar}>
          {statefullFields.map(({ ref, ...rest }, i) => {
            return (
              <Input
                {...rest}
                sidebar={sidebar}
                key={i}
                inputRef={ref}
                onChange={() => {
                  onChange(i);
                }}
                disabled={submitted || error}
              />
            );
          })}
        </Grid>
        {sidebar && <FormDisclaimer />}
        <Button
          form={true}
          sidebar={sidebar}
          title="EVALUATE MY CASE"
          formNoValidate
          type="submit"
        />
      </FormComponent>
      <AnimateHeight height={submitted || error ? "auto" : 0} duration={500}>
        <Response error={error}>
          {error
            ? "Sorry, but something went wrong. Try again later, or contact the office directly."
            : "Thank you for filling out our form! Someone from our office will be in contact with you shortly."}
        </Response>
      </AnimateHeight>
    </>
  );
};

export default Form;
